/** @jsxImportSource @emotion/react */

import { Checkbox, Icon, Tooltip, Typography } from "@a_team/ui-components";
import { BuilderAvatar } from "components/Collaborators/CollaboratorTable/BuilderAvatar";
import { RoleSelectorForCollaborator } from "components/Collaborators/CollaboratorTable/RoleSelectorForCollaborator";
import { CustomSelect } from "components/Collaborators/RoleSelector/CustomSelect";
import { useCSSRulesWithTheme } from "hooks/useCSSRulesWithTheme";
import { AccountMember } from "models/Account";
import { EmailSettings } from "models/Mission";
import { useMemo } from "react";
import { Flex } from "views/Builder/components/Flex";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { Flags } from "../../../configs/featureFlags";
import { getLabelForRole, Role } from "../../../configs/role";
import useUserRoleFromAccount from "../../../hooks/access/useUserRoleFromAccount";
import { isBillingContact } from "views/utils";
import { ClientSettingsWorkspaceLocation } from "Locations";
import { useHistory } from "react-router-dom";
import { TEAM_SUMMARY_UPDATES_HASH } from "components/Collaborators/constants";

export const CollaboratorCard = ({
  collaborator,
  missionSpecId,
  emailSettings = [],
  saving = false,
  onEmailSettingsChange,
  onHiringManagerChange,
  showHiringManagerCheckbox = false,
  allowEmailSettingsEdit = false,
  closeCollaboratorManageModal,
}: {
  collaborator: AccountMember;
  missionSpecId?: string;
  emailSettings?: EmailSettings[];
  saving?: boolean;
  onEmailSettingsChange?: (settings: EmailSettings[]) => void;
  onHiringManagerChange?: (checked: boolean) => void;
  showHiringManagerCheckbox?: boolean;
  allowEmailSettingsEdit?: boolean;
  closeCollaboratorManageModal?: () => void;
}) => {
  const styles = useCSSRulesWithTheme(({ colors }) => ({
    card: {
      border: `1px solid ${colors.Grey[200]}`,
      borderRadius: 4,
      overflow: "hidden",
      width: "100%",
    },
    cardRow: {
      paddingTop: 15,
      paddingBottom: 15,
      paddingLeft: 15,
      paddingRight: 15,
      borderBottom: `1px solid ${colors.Grey[300]}`,
      display: "flex",
      justifyContent: "space-between",
      ":last-child": {
        borderBottom: "none",
      },
    },
    link: {
      color: colors.Hannibal[600],
      cursor: "pointer",
    },
  }));

  const history = useHistory();

  const { flagEnabled: builderRatingsEnabled } = useFeatureFlag(
    Flags.BuilderRatings
  );

  const { flagEnabled: teamSummaryEmailEnabled } = useFeatureFlag(
    Flags.TeamSummaryEmail
  );

  const { flagEnabled: hideTeamPulseEmailSettingEnabled } = useFeatureFlag(
    Flags.HideTeamPulseEmailSettingEnabled
  );

  const hasMissionWriteAccess = [
    Role.TeamAdvisor,
    Role.WorkspaceAdmin,
    ...(missionSpecId
      ? [`${Role.MissionAdmin}:${missionSpecId}` as string as Role]
      : []),
  ].some((role) => collaborator.roles?.includes(role));

  const tooltip = (
    <Tooltip text="Hiring managers will get regular updates on team health.">
      <Icon name="details" size="md" />
    </Tooltip>
  );

  const labelForEmailSettings = useMemo(() => {
    if (emailSettings.length <= 0) {
      return "None";
    }
    return Object.keys(EmailSettings)
      .filter((key) =>
        emailSettings.includes(EmailSettings[key as keyof typeof EmailSettings])
      )
      .join(", ");
  }, [emailSettings]);

  const role = useUserRoleFromAccount({
    userId: collaborator.user.id,
    missionSpecId,
  });

  // We don't want to send the builder feedback or mission updates to billing contacts
  const isUserBillingContact = useMemo(
    () =>
      isBillingContact({
        email: collaborator.user.email,
        firstName: collaborator.user.firstName,
        lastName: collaborator.user.lastName,
      }),
    [
      collaborator.user.email,
      collaborator.user.firstName,
      collaborator.user.lastName,
    ]
  );

  const handleMissionUpdatesClick = () => {
    closeCollaboratorManageModal && closeCollaboratorManageModal();
    history.push(
      `${ClientSettingsWorkspaceLocation}${TEAM_SUMMARY_UPDATES_HASH}`
    );
  };

  return (
    <div css={styles.card}>
      <div css={styles.cardRow}>
        <Flex direction="row" gap={8}>
          <BuilderAvatar url={collaborator.user.profilePictureURL} />
          <div>
            <Typography variant="textMedium" component="div">
              {collaborator.user.fullName}
            </Typography>
            <Typography variant="textSmall" color="Grey@500" component="div">
              {collaborator.user.email}
            </Typography>
          </div>
        </Flex>
      </div>
      <div css={styles.cardRow}>
        <Typography variant="textMedium" weight="regular" component="div">
          Mission role
        </Typography>
        <div>
          {hasMissionWriteAccess ? (
            <RoleSelectorForCollaborator collaborator={collaborator} />
          ) : (
            <Typography variant="textMedium">
              {role
                ? getLabelForRole(role)
                : getLabelForRole(Role.MissionMember)}
            </Typography>
          )}
        </div>
      </div>
      {showHiringManagerCheckbox && (
        <div css={styles.cardRow}>
          <Flex gap={4}>
            <Typography variant="textMedium" weight="regular" component="div">
              Hiring manager
            </Typography>
            {tooltip}
          </Flex>
          <Checkbox
            checked={collaborator.attributes?.hiringManager || false}
            onChange={(e) => {
              onHiringManagerChange?.(e.target.checked);
            }}
          />
        </div>
      )}
      <div css={styles.cardRow}>
        <Typography variant="textMedium" weight="regular" component="div">
          Email settings
        </Typography>
        {allowEmailSettingsEdit ? (
          <Flex gap={4} alignItems="center">
            <CustomSelect
              value={emailSettings}
              onChange={(v) => {
                const newEmailSettings = Array.isArray(v) ? v : [v];
                onEmailSettingsChange?.(newEmailSettings as EmailSettings[]);
              }}
              multiple
              disabled={saving}
              title="Email settings"
              options={[
                ...(!hideTeamPulseEmailSettingEnabled
                  ? [
                      {
                        value: EmailSettings.TeamPulse,
                        label: "TeamPulse",
                        description:
                          "The collaborator will receive team member statuses and feedback twice a month.",
                      },
                    ]
                  : []),
                ...(hasMissionWriteAccess
                  ? [
                      {
                        value: EmailSettings.Invoices,
                        label: "Invoices",
                        description:
                          "The collaborator will receive invoices and billing updates.",
                      },
                    ]
                  : []),
                ...(builderRatingsEnabled
                  ? [
                      {
                        label: "Builder feedback",
                        value: EmailSettings.BuilderFeedback,
                        description:
                          "The collaborator will receive feedback requests for team members who have finished their work on the mission.",
                        disabled: isUserBillingContact,
                      },
                    ]
                  : []),
                {
                  label: "Team summaries",
                  value: EmailSettings.MissionUpdates,
                  description: teamSummaryEmailEnabled ? (
                    "Receive team updates to your inbox."
                  ) : (
                    <>
                      Summaries are disabled.{" "}
                      <span
                        css={styles.link}
                        onClick={handleMissionUpdatesClick}
                      >
                        Click here
                      </span>{" "}
                      to reenable for your workspace and get insights on team
                      progress.
                    </>
                  ),
                  disabled: isUserBillingContact || !teamSummaryEmailEnabled,
                },
              ]}
            >
              <Typography variant="textMedium" weight="bold">
                {labelForEmailSettings}
              </Typography>
            </CustomSelect>
            <Icon name="arrowForward" size="md" />
          </Flex>
        ) : (
          <Typography variant="textMedium" color={"Grey@400"}>
            No Access
          </Typography>
        )}
      </div>
    </div>
  );
};
